import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import ProjectForm from '../components/ProjectForm';
import TableFilter from '../components/TableFilter';
import ActionButtons from '../components/ActionButtons';
import DetailsPanel from '../components/DetailsPanel';
import ReportModal from '../components/ReportModal';

interface Project {
  id: number;
  title: string;
  description: string;
  origin: string;
  status: string;
  responsible?: number;
  responsible_name?: string;
  proposers?: { id: number; name: string }[];
  is_active?: boolean;
  expected_completion_date?: string;
  created_at: string;
}

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  // Modal de Relatórios
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; rowId?: number; isRowReport: boolean }>({
    isOpen: false,
    isRowReport: false
  });

  // Master-Detail State
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await api.get('/projects/');
      setProjects(response.data);
      setFilteredProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects", error);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = projects;

    if (filters.title) {
      result = result.filter(p => p.title.toLowerCase().includes(filters.title.toLowerCase()));
    }
    if (filters.origin) {
      result = result.filter(p => p.origin.toLowerCase().includes(filters.origin.toLowerCase()));
    }
    if (filters.status) {
      result = result.filter(p => p.status === filters.status);
    }

    setFilteredProjects(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingProject(null);
    fetchProjects();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProject(null);
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este projeto?')) {
      try {
        await api.delete(`/projects/${id}/`);
        fetchProjects();
        if (selectedProject?.id === id) setSelectedProject(null);
      } catch (error) {
        console.error("Error deleting project", error);
        alert('Erro ao excluir projeto.');
      }
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await api.patch(`/projects/${id}/`, { status: newStatus });
      fetchProjects();
    } catch (error) {
      console.error("Error updating status", error);
      alert('Erro ao atualizar status.');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Proposed': return 'badge-proposed';
      case 'InReview': return 'badge-in-review';
      case 'Approved': return 'badge-approved';
      case 'Implemented': return 'badge-implemented';
      case 'Rejected': return 'badge-rejected';
      default: return 'badge-secondary';
    }
  };

  const getStatusDotColor = (status: string) => {
    switch (status) {
      case 'Proposed': return '#94a3b8';
      case 'InReview': return '#f97316';
      case 'Approved': return '#16a34a';
      case 'Implemented': return '#ffffff';
      case 'Rejected': return '#dc2626';
      default: return '#94a3b8';
    }
  };

  const openGeneralReports = () => setReportModal({ isOpen: true, isRowReport: false });
  const openRowReport = (id: number) => setReportModal({ isOpen: true, rowId: id, isRowReport: true });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Projetos e Propostas</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Acompanhe as propostas de melhoria</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openGeneralReports}>Relatórios de Projetos</button>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>
              + Nova Proposta
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <ProjectForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            projectToEdit={editingProject}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'title', label: 'Título' },
            { key: 'origin', label: 'Origem' },
            {
              key: 'status', label: 'Status', type: 'select', options: [
                { value: 'Proposed', label: 'Proposto' },
                { value: 'InReview', label: 'Em Análise' },
                { value: 'Approved', label: 'Aprovado' },
                { value: 'Implemented', label: 'Implementado' },
                { value: 'Rejected', label: 'Rejeitado' }
              ]
            }
          ]}
          onFilter={handleFilter}
        />
      )}

      {reportModal.isOpen && (
        <ReportModal 
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ ...reportModal, isOpen: false })}
          category="Project"
          rowId={reportModal.rowId}
          isRowReport={reportModal.isRowReport}
        />
      )}

      <div className="page-container">
        <div className="list-container card" style={{ overflowX: 'auto', padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Título</th>
                <th>Origem</th>
                <th>Status</th>
                <th>Responsável</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                    Nenhum projeto encontrado.
                  </td>
                </tr>
              ) : (
                filteredProjects.map((project) => (
                  <tr
                    key={project.id}
                    onClick={() => setSelectedProject(project)}
                    className={selectedProject?.id === project.id ? 'selected' : ''}
                  >
                    <td style={{ fontWeight: '500' }}>{project.title}</td>
                    <td>{project.origin}</td>
                    <td>
                      <div className={`badge ${getStatusBadgeClass(project.status)}`} style={{ padding: '0', overflow: 'hidden', display: 'inline-flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <select
                          value={project.status}
                          onChange={(e) => handleStatusChange(project.id, e.target.value)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: 'inherit',
                            padding: '4px 8px 4px 24px',
                            cursor: 'pointer',
                            textTransform: 'uppercase',
                            appearance: 'none',
                            outline: 'none',
                            position: 'relative',
                            zIndex: 1
                          }}
                        >
                          <option value="Proposed">Proposto</option>
                          <option value="InReview">Em Análise</option>
                          <option value="Approved">Aprovado</option>
                          <option value="Implemented">Implementado</option>
                          <option value="Rejected">Rejeitado</option>
                        </select>
                        <span
                          className="status-dot"
                          style={{
                            backgroundColor: getStatusDotColor(project.status),
                            position: 'absolute',
                            left: '8px',
                            pointerEvents: 'none'
                          }}
                        />
                      </div>
                    </td>
                    <td>{project.responsible_name || '-'}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <ActionButtons
                        onReport={() => project.id && openRowReport(project.id)}
                        onEdit={() => handleEdit(project)}
                        onDelete={() => project.id && handleDelete(project.id)}
                        deletePermission="delete_project"
                      />

                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedProject && (
          <DetailsPanel title="Detalhes do Projeto" onClose={() => setSelectedProject(null)}>
            <div className="details-row">
              <div className="details-label">Título</div>
              <div className="details-value">{selectedProject.title}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Origem</div>
              <div className="details-value">{selectedProject.origin}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Responsável</div>
              <div className="details-value">{selectedProject.responsible_name || '-'}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Proponentes</div>
              <div className="details-value">
                {selectedProject.proposers && selectedProject.proposers.length > 0 ? (
                  selectedProject.proposers.map(p => (
                    <span key={p.id} className="badge badge-info" style={{ marginRight: '5px', marginBottom: '5px' }}>
                      {p.name}
                    </span>
                  ))
                ) : (
                  <span style={{ color: 'var(--text-light)' }}>-</span>
                )}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Previsão de Conclusão</div>
              <div className="details-value">
                {selectedProject.expected_completion_date ? new Date(selectedProject.expected_completion_date).toLocaleDateString() : '-'}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Data de Criação</div>
              <div className="details-value">
                {new Date(selectedProject.created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Descrição</div>
              <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
                {selectedProject.description || 'Sem descrição.'}
              </div>
            </div>
          </DetailsPanel>
        )}
      </div>
    </div>
  );
};

export default ProjectsPage;
